import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wind,
  CloudSun,
  MoonStar,
  Volume2,
  BookOpen,
  CheckSquare,
  Crosshair,
  Target,
  MessageCircle,
  Ruler,
  Newspaper,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SectionHeading } from "@/components/SectionHeading";
import { PhoneMockup } from "@/components/PhoneMockup";
import { SCREENS } from "@/data/content";

const ICONS = { Wind, CloudSun, MoonStar, Volume2, BookOpen, CheckSquare, Crosshair, Target, MessageCircle, Ruler, Newspaper };
const AUTOPLAY_MS = 5200;

export const Toolkit = () => {
  const { t } = useLanguage();
  const s = t.tools;
  const items = s.items;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const inView = useRef(false);
  const sectionRef = useRef(null);
  const chipRefs = useRef([]);

  const select = useCallback((i) => {
    setActive(i);
    setPaused(true);
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => {
      inView.current = e.isIntersecting;
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const id = setInterval(() => {
      if (inView.current) setActive((a) => (a + 1) % items.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused, items.length]);

  useEffect(() => {
    const chip = chipRefs.current[active];
    if (chip && chip.scrollIntoView) {
      chip.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
    }
  }, [active]);

  const current = items[active];
  const CurrentIcon = ICONS[current.icon] || Wind;
  const screenSrc = current.screen ? SCREENS[current.screen] : null;

  return (
    <section id="werkzeuge" ref={sectionRef} className="relative scroll-mt-24 py-24 md:py-32">
      <div className="hairline absolute inset-x-0 top-0 mx-auto max-w-5xl" />
      <div className="mx-auto max-w-7xl px-5 sm:px-6 md:px-8">
        <SectionHeading index={s.index} eyebrow={s.eyebrow} title={s.title} body={s.body} />

        {/* mobile chips */}
        <div className="no-scrollbar -mx-5 mt-10 flex gap-2 overflow-x-auto px-5 pb-2 lg:hidden" role="tablist" aria-label={s.hint}>
          {items.map((item, i) => {
            const Icon = ICONS[item.icon] || Wind;
            const isActive = i === active;
            return (
              <button
                key={item.id}
                ref={(el) => (chipRefs.current[i] = el)}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => select(i)}
                data-testid={`tool-chip-${item.id}`}
                className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "border-[var(--sage)] bg-[var(--sage)]/10 text-white"
                    : "border-[var(--line-strong)] text-[var(--ink-2)]"
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.title}
              </button>
            );
          })}
        </div>

        <div className="mt-8 grid items-start gap-10 lg:mt-16 lg:grid-cols-12 lg:gap-12">
          {/* desktop list */}
          <div className="hidden lg:col-span-6 lg:block xl:col-span-5" role="tablist" aria-label={s.hint}>
            <p className="eyebrow mb-4 !text-[0.62rem] text-[var(--ink-3)]">{s.hint}</p>
            <ul className="divide-y divide-[var(--line)] border-y border-[var(--line)]">
              {items.map((item, i) => {
                const Icon = ICONS[item.icon] || Wind;
                const isActive = i === active;
                return (
                  <li key={item.id} className="relative">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => select(i)}
                      onMouseEnter={() => setPaused(true)}
                      data-testid={`tool-tab-${item.id}`}
                      className="group flex w-full items-center gap-4 py-3.5 text-left"
                    >
                      <span className="w-6 font-mono text-[11px] text-[var(--ink-3)]">{String(i + 1).padStart(2, "0")}</span>
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                          isActive ? "border-[var(--sage)]/60 bg-[var(--olive)]/20" : "border-[var(--line)] bg-[var(--surface)] group-hover:border-[var(--line-strong)]"
                        }`}
                      >
                        <Icon className={`h-4.5 w-4.5 ${isActive ? "text-[var(--sage)]" : "text-[var(--ink-2)]"}`} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={`block font-semibold ${isActive ? "text-white" : "text-[var(--ink)]"}`}>{item.title}</span>
                        <span className="block truncate text-sm text-[var(--ink-3)]">{item.short}</span>
                      </span>
                      {!item.screen && (
                        <span className="rounded-full border border-dashed border-[var(--line-strong)] px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-[var(--ink-3)]">
                          {t.placeholder.note}
                        </span>
                      )}
                    </button>
                    <AnimatePresence initial={false}>
                      {isActive && (
                        <motion.div
                          key="desc"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
                          className="overflow-hidden"
                        >
                          <p className="pb-5 pl-[4.75rem] pr-4 text-sm leading-relaxed text-[var(--ink-2)]">{item.desc}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    {isActive && (
                      <motion.span
                        layoutId="tool-active-bar"
                        className="absolute -left-px top-3 bottom-3 w-px bg-[var(--sage)]"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* device */}
          <div className="lg:col-span-6 xl:col-span-7">
            <div className="relative mx-auto flex max-w-[860px] flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-center lg:gap-12">
              <div className="relative w-[260px] shrink-0 sm:w-[290px]">
                <div className="absolute -inset-10 rounded-full bg-[var(--olive)]/15 blur-3xl" aria-hidden="true" />
                <div className="relative">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={current.id}
                      initial={{ opacity: 0, y: 14, scale: 0.985 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.985 }}
                      transition={{ duration: 0.38, ease: [0.2, 0.7, 0.2, 1] }}
                    >
                      <PhoneMockup src={screenSrc} alt={current.title} label={current.title} testId={`tool-phone-${current.id}`} />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              {/* mobile / side description card */}
              <div className="w-full max-w-md lg:hidden">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.3 }}
                    className="card p-6"
                    data-testid="tool-mobile-desc"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--sage)]/60 bg-[var(--olive)]/20">
                        <CurrentIcon className="h-4.5 w-4.5 text-[var(--sage)]" />
                      </span>
                      <div>
                        <p className="font-semibold text-white">{current.title}</p>
                        <p className="text-xs text-[var(--ink-3)]">{current.short}</p>
                      </div>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-[var(--ink-2)]">{current.desc}</p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
